import React, { useMemo, useState, memo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";

const normalizeData = (data = []) =>
  data.map((item) => ({
    ...item,
    value: item.value ?? item.count ?? item.attendance ?? item.present ?? 0,
  }));

function HorizontalBarChart({
  title = "Chart",
  data = [],
  color = "#2ec4b6",
  loading = false,
  height = 320,
  xAxisLabelKey = "month",
}) {
  const [activeBar, setActiveBar] = useState(null);

  const chartData = useMemo(() => normalizeData(data), [data]);

  const hasData = chartData.some((d) => d.value > 0);

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      {/* Header */}
      <p className="font-bold text-lg mb-4">{title}</p>
      <hr className="dashboard-divider" />

      {/* Loading */}
      {loading ? (
        <div className="w-full h-[60%] flex items-center justify-center bg-gray-50 border rounded-lg p-10 animate-pulse">
          <span className="text-gray-500 font-medium">Loading Chart...</span>
        </div>
      ) : !hasData ? (
        <div className="h-[260px] flex items-center justify-center text-gray-500 font-medium">
          No Data Available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
          >
            <CartesianGrid stroke="#e0e0e0" strokeDasharray="3 3" />

            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "#444" }}
            />

            <YAxis
              dataKey={xAxisLabelKey}
              type="category"
              width={90}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "#444" }}
            />

            <Tooltip
              cursor={{ fill: "transparent" }}
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;

                const value = payload[0].value;

                return (
                  <div className="bg-[#23272f] text-white rounded-lg px-3 py-2 text-sm font-medium shadow-lg">
                    <div> {value} Students</div>
                  </div>
                );
              }}
            />

            <Bar dataKey="value" barSize={25} radius={[0, 6, 6, 0]}>
              {chartData.map((_, index) => (
                <Cell
                  key={index}
                  fill={color}
                  fillOpacity={
                    activeBar === null || activeBar === index ? 1 : 0.25
                  }
                  onMouseEnter={() => setActiveBar(index)}
                  onMouseLeave={() => setActiveBar(null)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default memo(HorizontalBarChart);
